import { Fragment, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  useCollectionBinding,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoBlack,
  FacebookIcon,
  InstagramIcon,
  YouTubeIcon,
  TikTokIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Black variant of the Repli wordmark for use on the footer's light
// background. Source SVG mirrors `repli_white.svg` with the wordmark's
// `fill:#fff` swapped to `#1a1a1a`.
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Types & helpers
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by their canonical day key so we can look each
  // day up when walking the full week below.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];

  // Walk every day of the week. Days without provided hours (e.g. Sunday)
  // render as "Closed" rather than being omitted.
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the renderer can pick whichever fits the layout.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    // Light footer background → default to the dark/black mono logo variant.
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_black_logo', suggestedFields: ['property_company_mono_black_logo', 'property_company_logo', 'property_company_mono_white_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Property Address, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_youtube_url', suggestedFields: ['property_youtube_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_tiktok_url', suggestedFields: ['property_tiktok_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  // No dedicated management-company logo? Fall back to the property's secondary/
  // company logo — the same image the header's hamburger drawer shows.
  const secondaryLogo = usePropertyField('property_secondary_logo');
  const managedByImage = managedByLogo.image || secondaryLogo;
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const youtubeLink = useSlotGroup('footer.youtube_link', youtubeLinkDef);
  const tiktokLink = useSlotGroup('footer.tiktok_link', tiktokLinkDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  const hasSocialLinks = Boolean(
    facebookLink.url || instagramLink.url || youtubeLink.url || tiktokLink.url,
  );

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Split the address on its first comma so the street sits on line 1 and the
  // city/state/zip falls to line 2 (matches the stacked layout in the design).
  const addressText = addressLink.text || '123 Property Address, City, ST 00000';
  const commaIndex = addressText.indexOf(',');
  const addressLine1 = commaIndex !== -1 ? addressText.slice(0, commaIndex) : addressText;
  const addressLine2 = commaIndex !== -1 ? addressText.slice(commaIndex + 1).trim() : '';

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe Connect widget is injected at runtime and sets its own
  // inline `style="z-index: …"` on the launcher after mount, which beats a
  // normal stylesheet rule. The CSS rule further down covers the known
  // selectors as a cheap first pass; this effect is the authoritative fix via
  // `setProperty(..., 'important')`.
  //
  // Scoped tightly to the real launcher nodes only (`.rcc-app-cta-wrap`,
  // `.rcc-app-controls`, `#conversations-webchat-toggle`). We pin a fixed
  // ancestor only when that ancestor is the chat wrap itself — never a
  // broader outermost fixed container. Z sits just above the open header /
  // offer-banner stack (100).
  //
  // Mounted from Footer because Footer renders on every page via
  // DefaultLayout, giving a single global hook without a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const Z = '120';

    function isLauncher(el: Element): boolean {
      return el.matches(LAUNCHER_SELECTOR);
    }

    function isChatWrap(el: Element): boolean {
      return el instanceof HTMLElement && el.classList.contains('rcc-app-cta-wrap');
    }

    /** Walk up for a `position: fixed` ancestor that is the chat wrap. */
    function fixedChatWrapAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el.parentElement;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        if (isChatWrap(cur) && getComputedStyle(cur).position === 'fixed') {
          return cur;
        }
        cur = cur.parentElement;
      }
      return null;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!isLauncher(el)) return;
      pin(el);
      // Only lift a fixed ancestor when it is the real chat wrap — do not pin
      // arbitrary outermost fixed portals.
      if (!isChatWrap(el)) {
        const wrap = fixedChatWrapAncestor(el);
        if (wrap) pin(wrap);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-[var(--vibe-color-text)]"
      style={{ backgroundColor: 'var(--vibe-color-background)' }}
    >
      {/* ---- Main Footer Content: light surface block ----
          White edges frame the gray block via responsive padding on the
          background-colored wrapper (the footer's white shows through), the
          same boxed treatment the ImageCallouts section uses. */}
      <div className="bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 py-3 sm:py-5 md:py-6">
        {/* Fixed off-white — intentionally not a theme token so brand
            surface swaps don't recolor this footer block. */}
        <div style={{ backgroundColor: '#F5F3EF' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

          {/* Logo + social, centered above the divider */}
          <div className="flex flex-col items-center gap-6 mb-10">
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-16 w-auto max-w-[200px] object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl text-[var(--vibe-color-text)]">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {hasSocialLinks ? (
              <nav className="flex items-center gap-4" aria-label="Social media">
                {facebookLink.url ? (
                  <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                    <a
                      href={facebookLink.url}
                      target={facebookLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={facebookLink.label || 'Facebook'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <FacebookIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
                {instagramLink.url ? (
                  <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                    <a
                      href={instagramLink.url}
                      target={instagramLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={instagramLink.label || 'Instagram'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <InstagramIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
                {youtubeLink.url ? (
                  <EditableSlotGroup groupPath="footer.youtube_link" definition={youtubeLinkDef}>
                    <a
                      href={youtubeLink.url}
                      target={youtubeLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={youtubeLink.label || 'YouTube'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <YouTubeIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
                {tiktokLink.url ? (
                  <EditableSlotGroup groupPath="footer.tiktok_link" definition={tiktokLinkDef}>
                    <a
                      href={tiktokLink.url}
                      target={tiktokLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={tiktokLink.label || 'TikTok'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <TikTokIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
              </nav>
            ) : null}
          </div>

          {/* Top divider */}
          <hr className="border-t border-[var(--vibe-color-border)]" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mt-10 md:mt-14">

            {/* Contact */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.contact_label"
                defaultValue="<p>Contact</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors"
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Location */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.location_label"
                defaultValue="<p>Location</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors block leading-relaxed"
                >
                  <span className="block">{addressLine1}</span>
                  {addressLine2 ? <span className="block">{addressLine2}</span> : null}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Hours */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.hours_label"
                defaultValue="<p>Hours</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              {groupedHours.length > 0 ? (
                // Single grid so the day and time columns line up across every
                // row; `self-center` centers the aligned block under the "Hours" label.
                <dl className="self-center grid grid-cols-[auto_auto] gap-x-6 gap-y-2 text-left font-body text-sm text-[var(--vibe-color-text-muted)]">
                  {groupedHours.map((group, index) => {
                    // Abbreviate multi-day ranges ("Mon - Fri"); keep single
                    // days spelled out ("Saturday") to match the design.
                    const dayDisplay = group.label.includes(' - ') ? group.shortLabel : group.label;
                    return (
                      <Fragment key={index}>
                        <dt className="text-left">{dayDisplay}</dt>
                        <dd className="text-left">{group.hours}</dd>
                      </Fragment>
                    );
                  })}
                </dl>
              ) : (
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)]/60">Office hours will be displayed here.</p>
              )}
            </div>

          </div>
          </div>
        </div>
      </div>

      {/* ---- Compliance Icons + Legal Nav ---- */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-3">
        <div className="flex flex-col items-center gap-4">
          {/* Compliance icons in a row */}
          <div className="flex items-center justify-center gap-8 text-[var(--vibe-color-text)]">
            <FairHousingIcon size={36} color="currentColor" />
            <PetsAllowedIcon size={32} color="currentColor" />
            <WheelchairIcon size={32} color="currentColor" />
          </div>

          {/* Legal nav links with dot separator */}
          <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-body text-[13px] text-[var(--vibe-color-text-muted)]" aria-label="Legal links">
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-muted)]/50">·</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-muted)]/50">·</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>
        </div>
      </div>

      {/* ---- Bottom Credit Bar: Managed By / Powered By / Design By / Copyright ---- */}
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-6">
          <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-5 font-body text-[10px] text-[var(--vibe-color-text-muted)]">

            {/* Managed By */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={managedByLogoDef}
              className="flex justify-center"
            >
              {managedByImage ? (
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Professionally Managed by</p>"
                    as="span"
                    className="text-[var(--vibe-color-text-muted)]"
                  />
                  <VibesitesImage
                    url={managedByImage}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    className="h-4 w-auto max-w-[110px] object-contain"
                  />
                </a>
              ) : null}
            </EditableSlotGroup>

            {/* Powered By (Multihub) */}
            <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="text-[var(--vibe-color-text-muted)]"
                />
                <MultihubLogoMonoBlack height={14} alt={poweredByLogo.alt || 'Multihub'} />
              </a>
            </EditableSlotGroup>

            {/* Website Design By (Repli) */}
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="text-[var(--vibe-color-text-muted)]"
                />
                <VibesitesImage
                  url={repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={16}
                  className="h-4 w-auto"
                  priority
                />
              </a>
            </EditableSlotGroup>

            {/* Copyright — year auto-updates from the runtime clock so the
                footer never goes stale. Full-width centered row on mobile;
                inline on desktop. */}
            <span className="w-full text-center md:w-auto">
              © {new Date().getFullYear()} All Rights Reserved.
            </span>

          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe Connect launcher ----
          Cheap first pass for the known launcher nodes. The authoritative fix
          lives in the `useEffect` above — MutationObserver + inline
          `!important` to beat the widget's own post-mount inline z-index.
          Kept just above the open header / offer-banner stack (100). */}
      <style>{`
        /* Production RentCafe Connect build: floating wrap is
           .rcc-app-cta-wrap, controls layer .rcc-app-controls, launcher
           #conversations-webchat-toggle. Target only those — not broad
           yardi / rentcafe iframe or text matches. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact</p>',
    },
    'footer.location_label': {
      label: 'Location Label',
      type: 'richtext',
      defaultValue: '<p>Location</p>',
    },
    'footer.hours_label': {
      label: 'Hours Label',
      type: 'richtext',
      defaultValue: '<p>Hours</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.youtube_link': youtubeLinkDef,
    'footer.tiktok_link': tiktokLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
